/* eslint-disable import/prefer-default-export */
import { camelCaseObject, getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { convertObjectToSnakeCase } from '../../utils';

const getApiBaseUrl = () => getConfig().STUDIO_BASE_URL;
export const getCourseDetailsApiUrl = (courseId) => `${getApiBaseUrl()}/api/contentstore/v1/course_details/${courseId}`;
export const getCourseSettingsApiUrl = (courseId) => `${getApiBaseUrl()}/api/contentstore/v1/course_settings/${courseId}`;
export const getUploadAssetsUrl = (courseId) => `${getApiBaseUrl()}/assets/${courseId}/`;
export const getIblCourseSettingsApiUrl = () => `${getApiBaseUrl()}/api/ibl/v1/course_settings`;
export const getIblCopyMetadataApiUrl = () => `${getApiBaseUrl()}/api/ibl/v1/copy_course_metadata`;

/**
 * Get course details.
 * @param {string} courseId
 * @returns {Promise<Object>}
 */
export async function getCourseDetails(courseId) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getCourseDetailsApiUrl(courseId)}`,
  );
  return camelCaseObject(data);
}

/**
 * Update course details.
 * @param {string} courseId
 * @param {object} details
 * @returns {Promise<Object>}
 */
export async function updateCourseDetails(courseId, details) {
  const { data } = await getAuthenticatedHttpClient().put(
    `${getCourseDetailsApiUrl(courseId)}`,
    convertObjectToSnakeCase(details, true),
  );
  return camelCaseObject(data);
}

/**
 * Get course settings.
 * @param {string} courseId
 * @returns {Promise<Object>}
 */
export async function getCourseSettings(courseId) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getCourseSettingsApiUrl(courseId)}`,
  );
  return camelCaseObject(data);
}

/**
 * Get IBL course settings (primary source for all editable S&D fields).
 * @param {string} courseId
 * @returns {Promise<{ formData: Object, formChoices: Object }>}
 */
export async function getIblCourseSettings(courseId) {
  const { data } = await getAuthenticatedHttpClient().get(
    getIblCourseSettingsApiUrl(),
    { params: { course_key: courseId } },
  );
  return {
    formData: camelCaseObject(data.formData),
    formChoices: data.formChoices,
  };
}

/**
 * Save all course settings via the IBL endpoint (replaces contentstore PUT).
 * @param {string} courseId
 * @param {object} details
 * @returns {Promise<Object>}
 */
export async function updateIblCourseSettings(courseId, details) {
  const { data } = await getAuthenticatedHttpClient().post(
    getIblCourseSettingsApiUrl(),
    { course_key: courseId, ...convertObjectToSnakeCase(details, true) },
  );
  return camelCaseObject(data);
}

/**
 * Copy all metadata from a source course into the current course.
 * @param {string} sourceCourseId
 * @param {string} targetCourseId
 * @returns {Promise<{ success: boolean, message: string } | { error: string }>}
 */
export async function copyIblCourseMetadata(sourceCourseId, targetCourseId) {
  const { data } = await getAuthenticatedHttpClient().post(
    getIblCopyMetadataApiUrl(),
    { source_course_key: sourceCourseId, target_course_key: targetCourseId },
  );
  return data;
}
